import React, { useState, useEffect } from 'react';
import { Save, Plus, Trash2, Upload } from 'lucide-react';
import api from '../../services/api';
import StandardPremiumModal from './StandardPremiumModal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'react-hot-toast';

const TABS = [
  { id: 'basic', label: 'Basic' },
  { id: 'business', label: 'Business' },
  { id: 'address', label: 'Address' },
  { id: 'credit', label: 'Credit & Terms' },
  { id: 'contacts', label: 'Contact Persons' },
  { id: 'other', label: 'Other' },
];

const SALUTATIONS = ['Mr.', 'Ms.', 'Mrs.', 'Dr.', 'Prof.'];
const LANGUAGES = ['English', 'Hindi', 'Tamil', 'Telugu', 'Marathi', 'Bengali', 'Gujarati'];
const PAYMENT_TERMS = ['Due on Receipt', 'Net 7', 'Net 15', 'Net 30', 'Net 45', 'Net 60', 'Custom'];

const EnhancedAddPartyModal = ({ isOpen, onClose, party, partyType, businessId }) => {
  const [activeTab, setActiveTab] = useState('basic');
  const [loading, setLoading] = useState(false);
  const [contactPersons, setContactPersons] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});

  const [formData, setFormData] = useState({
    // Auto-set based on page (no UI selector)
    type: partyType || 'customer', // customer or supplier
    
    // Basic Tab - Enhanced
    salutation: '',
    first_name: '',
    last_name: '',
    display_name: '', // Required
    name: '', // Mapped to display_name
    mobile: '',
    work_phone: '',
    email: '',
    language: 'English',
    
    // Business Tab - Enhanced
    company_name: '',
    gstin: '',
    pan: '',
    currency: 'INR',
    payment_terms: 'Net 30',
    custom_payment_days: 0,
    enable_portal: false,
    website: '',
    department: '',
    designation: '',
    
    // Social Links
    twitter: '',
    skype: '',
    facebook: '',
    
    // Address Tab - Enhanced
    billing_address: {
      line1: '',
      line2: '',
      city: '',
      state: '',
      pincode: '',
      country: 'India',
    },
    shipping_address: {
      line1: '',
      line2: '',
      city: '',
      state: '',
      pincode: '',
      country: 'India',
    },
    shipping_same_as_billing: true,
    
    // Credit & Terms
    opening_balance_amount: 0,
    opening_balance_type: 'TO_COLLECT',
    credit_period_days: 30,
    credit_limit: 0,
    
    // Other
    notes: '',
    custom_fields: {},
    reporting_tags: [],
    
    // System
    status: 'active',
  });

  useEffect(() => {
    if (party) {
      setFormData(prev => ({
        ...prev,
        ...party,
        type: partyType || party.type || 'customer',
      }));
      
      if (party.contact_persons) {
        setContactPersons(party.contact_persons);
      }
    } else {
      // New party - set type from prop
      setFormData(prev => ({ ...prev, type: partyType || 'customer' }));
    }
  }, [party, partyType]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Auto-populate display_name
    if (field === 'first_name' || field === 'last_name') {
      const firstName = field === 'first_name' ? value : formData.first_name;
      const lastName = field === 'last_name' ? value : formData.last_name;
      const displayName = `${firstName} ${lastName}`.trim();
      setFormData(prev => ({ ...prev, display_name: displayName, name: displayName }));
    }
  };

  const handleAddressChange = (type, field, value) => {
    setFormData(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: value
      }
    }));
  };

  const handleCopyBillingToShipping = () => {
    setFormData(prev => ({
      ...prev,
      shipping_address: { ...prev.billing_address },
      shipping_same_as_billing: true
    }));
    toast.success('Billing address copied to shipping');
  };

  const handleAddContactPerson = () => {
    setContactPersons([...contactPersons, {
      id: Date.now(),
      salutation: '',
      first_name: '',
      last_name: '',
      email: '',
      work_phone: '',
      mobile: ''
    }]);
  };

  const handleRemoveContactPerson = (id) => {
    setContactPersons(contactPersons.filter(cp => cp.id !== id));
  };

  const handleContactPersonChange = (id, field, value) => {
    setContactPersons(contactPersons.map(cp => 
      cp.id === id ? { ...cp, [field]: value } : cp
    ));
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => file.size <= 10 * 1024 * 1024); // 10MB
    
    if (validFiles.length < files.length) {
      toast.error('Some files exceeded 10MB limit');
    }
    
    if (documents.length + validFiles.length > 10) {
      toast.error('Maximum 10 files allowed');
      return;
    }
    
    setDocuments([...documents, ...validFiles.map(f => ({ file: f, name: f.name }))]);
  };

  const handleSave = async () => {
    // Client-side Validation
    const errors = {};
    
    if (!formData.display_name && !formData.name && !formData.first_name) {
      errors.display_name = 'Display Name (or First Name) is required';
    }
    
    if (!formData.mobile && !formData.email) {
      errors.mobile = 'Mobile or Email is required';
      errors.email = 'Mobile or Email is required';
    }
    
    if (formData.email && formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email address';
    }
    
    setFieldErrors(errors);
    
    if (Object.keys(errors).length > 0) {
      const errorFields = Object.keys(errors).map(k => k.replace('_', ' ')).join(', ');
      toast.error(`Please fix required fields: ${errorFields}`);
      setActiveTab('basic');
      return;
    }

    setLoading(true);
    try {
      // Clean payload - convert empty strings to null
      const cleanPayload = (obj) => {
        const cleaned = {};
        for (const [key, value] of Object.entries(obj)) {
          if (value === '' || value === undefined) {
            cleaned[key] = null;
          } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
            cleaned[key] = cleanPayload(value);
          } else {
            cleaned[key] = value;
          }
        }
        return cleaned;
      };

      const payload = cleanPayload({
        ...formData,
        name: formData.display_name || formData.name || `${formData.first_name || ''} ${formData.last_name || ''}`.trim(),
        contact_persons: contactPersons,
      });

      if (party) {
        await api.put(`/parties/${party.party_id || party.id}`, payload);
      } else {
        await api.post('/parties', payload);
      }

      toast.success(party ? 'Updated successfully' : 'Added successfully');
      onClose(true); // true = refresh list
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail) {
        if (Array.isArray(detail)) {
          const fieldErrors = detail.map(e => {
            const field = e.loc ? e.loc[e.loc.length - 1] : 'field';
            return `${field}: ${e.msg}`;
          });
          toast.error(fieldErrors.join('; '));
        } else {
          toast.error(detail);
        }
      } else {
        toast.error(error.message || 'Failed to save contact');
      }
      console.error('Save error:', error);
    } finally {
      setLoading(false);
    }
  };

  const modalTitle = party 
    ? `Edit ${partyType === 'customer' ? 'Customer' : 'Supplier'}`
    : `Add ${partyType === 'customer' ? 'Customer' : 'Supplier'}`;

  const footerButtons = (
    <>
      <Button variant="outline" onClick={() => onClose(false)}>
        Cancel
      </Button>
      <Button onClick={handleSave} disabled={loading} className="bg-teal-600 hover:bg-teal-700">
        {loading ? 'Saving...' : (
          <>
            <Save className="w-4 h-4 mr-2" />
            {party ? 'Update' : 'Save'}
          </>
        )}
      </Button>
    </>
  );

  return (
    <StandardPremiumModal
      isOpen={isOpen}
      onClose={() => onClose(false)}
      title={modalTitle}
      tabs={TABS}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      footer={footerButtons}
    >
          {/* BASIC TAB */}
          {activeTab === 'basic' && (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-4">Primary Contact</h3>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Salutation</Label>
                  <Select value={formData.salutation} onValueChange={(v) => handleInputChange('salutation', v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {SALUTATIONS.map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>First Name *</Label>
                  <Input
                    value={formData.first_name}
                    onChange={(e) => handleInputChange('first_name', e.target.value)}
                    placeholder="Enter first name"
                  />
                </div>
                
                <div>
                  <Label>Last Name</Label>
                  <Input
                    value={formData.last_name}
                    onChange={(e) => handleInputChange('last_name', e.target.value)}
                    placeholder="Enter last name"
                  />
                </div>
              </div>

              <div>
                <Label className={fieldErrors.display_name ? 'text-red-600' : ''}>Display Name *</Label>
                <Input
                  value={formData.display_name}
                  onChange={(e) => {
                    handleInputChange('display_name', e.target.value);
                    setFieldErrors(prev => ({ ...prev, display_name: null }));
                  }}
                  placeholder="Auto-filled from First + Last Name"
                  className={fieldErrors.display_name ? 'border-red-500 focus:border-red-500' : ''}
                />
                {fieldErrors.display_name && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.display_name}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className={fieldErrors.email ? 'text-red-600' : ''}>Email Address</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => {
                      handleInputChange('email', e.target.value);
                      setFieldErrors(prev => ({ ...prev, email: null }));
                    }}
                    placeholder="email@example.com"
                    className={fieldErrors.email ? 'border-red-500 focus:border-red-500' : ''}
                  />
                  {fieldErrors.email && (
                    <p className="text-red-500 text-xs mt-1">{fieldErrors.email}</p>
                  )}
                </div>
                
                <div>
                  <Label>Language</Label>
                  <Select value={formData.language} onValueChange={(v) => handleInputChange('language', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LANGUAGES.map(lang => (
                        <SelectItem key={lang} value={lang}>{lang}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Work Phone</Label>
                  <Input
                    value={formData.work_phone}
                    onChange={(e) => handleInputChange('work_phone', e.target.value)}
                    placeholder="+91 XXXXXXXXXX"
                  />
                </div>
                
                <div>
                  <Label className={fieldErrors.mobile ? 'text-red-600' : ''}>Mobile</Label>
                  <Input
                    value={formData.mobile}
                    onChange={(e) => {
                      handleInputChange('mobile', e.target.value);
                      setFieldErrors(prev => ({ ...prev, mobile: null }));
                    }}
                    placeholder="+91 XXXXXXXXXX"
                    className={fieldErrors.mobile ? 'border-red-500 focus:border-red-500' : ''}
                  />
                  {fieldErrors.mobile && (
                    <p className="text-red-500 text-xs mt-1">{fieldErrors.mobile}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* BUSINESS TAB */}
          {activeTab === 'business' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Company Name</Label>
                  <Input
                    value={formData.company_name}
                    onChange={(e) => handleInputChange('company_name', e.target.value)}
                  />
                </div>
                
                <div>
                  <Label>Currency</Label>
                  <Select value={formData.currency} onValueChange={(v) => handleInputChange('currency', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="INR">INR - Indian Rupee</SelectItem>
                      <SelectItem value="USD">USD - US Dollar</SelectItem>
                      <SelectItem value="EUR">EUR - Euro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>GSTIN</Label>
                  <Input
                    value={formData.gstin}
                    onChange={(e) => handleInputChange('gstin', e.target.value)}
                    placeholder="22AAAAA0000A1Z5"
                  />
                </div>
                
                <div>
                  <Label>PAN</Label>
                  <Input
                    value={formData.pan}
                    onChange={(e) => handleInputChange('pan', e.target.value)}
                    placeholder="AAAAA0000A"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Payment Terms</Label>
                  <Select value={formData.payment_terms} onValueChange={(v) => handleInputChange('payment_terms', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_TERMS.map(term => (
                        <SelectItem key={term} value={term}>{term}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {formData.payment_terms === 'Custom' && (
                  <div>
                    <Label>Custom Days</Label>
                    <Input
                      type="number"
                      value={formData.custom_payment_days}
                      onChange={(e) => handleInputChange('custom_payment_days', parseInt(e.target.value))}
                    />
                  </div>
                )}
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  checked={formData.enable_portal}
                  onCheckedChange={(checked) => handleInputChange('enable_portal', checked)}
                />
                <Label>Enable Portal Access</Label>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Website URL</Label>
                  <Input
                    value={formData.website}
                    onChange={(e) => handleInputChange('website', e.target.value)}
                    placeholder="https://example.com"
                  />
                </div>
                
                <div>
                  <Label>Department</Label>
                  <Input
                    value={formData.department}
                    onChange={(e) => handleInputChange('department', e.target.value)}
                  />
                </div>
              </div>

              <div>
                <Label>Designation</Label>
                <Input
                  value={formData.designation}
                  onChange={(e) => handleInputChange('designation', e.target.value)}
                />
              </div>

              <h3 className="text-sm font-semibold text-gray-700 mt-6 mb-3">Social Links</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>X (Twitter)</Label>
                  <Input
                    value={formData.twitter}
                    onChange={(e) => handleInputChange('twitter', e.target.value)}
                    placeholder="@username"
                  />
                </div>
                
                <div>
                  <Label>Skype</Label>
                  <Input
                    value={formData.skype}
                    onChange={(e) => handleInputChange('skype', e.target.value)}
                  />
                </div>
                
                <div>
                  <Label>Facebook</Label>
                  <Input
                    value={formData.facebook}
                    onChange={(e) => handleInputChange('facebook', e.target.value)}
                  />
                </div>
              </div>

              <div>
                <Label>Documents (Max 10 files, 10MB each)</Label>
                <div className="mt-2 flex items-center gap-2">
                  <input
                    type="file"
                    multiple
                    onChange={handleFileUpload}
                    className="hidden"
                    id="file-upload"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => document.getElementById('file-upload').click()}
                    disabled={documents.length >= 10}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Files
                  </Button>
                  <span className="text-sm text-gray-500">{documents.length}/10 files</span>
                </div>
                {documents.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {documents.map((doc, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm bg-gray-50 p-2 rounded">
                        <span>{doc.name}</span>
                        <button onClick={() => setDocuments(documents.filter((_, i) => i !== idx))}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ADDRESS TAB */}
          {activeTab === 'address' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Billing Address</h3>
                <div className="space-y-3">
                  <Input
                    placeholder="Address Line 1"
                    value={formData.billing_address.line1}
                    onChange={(e) => handleAddressChange('billing_address', 'line1', e.target.value)}
                  />
                  <Input
                    placeholder="Address Line 2"
                    value={formData.billing_address.line2}
                    onChange={(e) => handleAddressChange('billing_address', 'line2', e.target.value)}
                  />
                  <div className="grid grid-cols-3 gap-3">
                    <Input
                      placeholder="City"
                      value={formData.billing_address.city}
                      onChange={(e) => handleAddressChange('billing_address', 'city', e.target.value)}
                    />
                    <Input
                      placeholder="State"
                      value={formData.billing_address.state}
                      onChange={(e) => handleAddressChange('billing_address', 'state', e.target.value)}
                    />
                    <Input
                      placeholder="Pincode"
                      value={formData.billing_address.pincode}
                      onChange={(e) => handleAddressChange('billing_address', 'pincode', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-700">Shipping Address</h3>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleCopyBillingToShipping}
                  >
                    Copy from Billing
                  </Button>
                </div>
                
                <div className="flex items-center space-x-2 mb-3">
                  <Checkbox
                    checked={formData.shipping_same_as_billing}
                    onCheckedChange={(checked) => handleInputChange('shipping_same_as_billing', checked)}
                  />
                  <Label>Same as Billing Address</Label>
                </div>

                {!formData.shipping_same_as_billing && (
                  <div className="space-y-3">
                    <Input
                      placeholder="Address Line 1"
                      value={formData.shipping_address.line1}
                      onChange={(e) => handleAddressChange('shipping_address', 'line1', e.target.value)}
                    />
                    <Input
                      placeholder="Address Line 2"
                      value={formData.shipping_address.line2}
                      onChange={(e) => handleAddressChange('shipping_address', 'line2', e.target.value)}
                    />
                    <div className="grid grid-cols-3 gap-3">
                      <Input
                        placeholder="City"
                        value={formData.shipping_address.city}
                        onChange={(e) => handleAddressChange('shipping_address', 'city', e.target.value)}
                      />
                      <Input
                        placeholder="State"
                        value={formData.shipping_address.state}
                        onChange={(e) => handleAddressChange('shipping_address', 'state', e.target.value)}
                      />
                      <Input
                        placeholder="Pincode"
                        value={formData.shipping_address.pincode}
                        onChange={(e) => handleAddressChange('shipping_address', 'pincode', e.target.value)}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* CREDIT & TERMS TAB */}
          {activeTab === 'credit' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Opening Balance</Label>
                  <Input
                    type="number"
                    value={formData.opening_balance_amount}
                    onChange={(e) => handleInputChange('opening_balance_amount', parseFloat(e.target.value))}
                  />
                </div>
                
                <div>
                  <Label>Balance Type</Label>
                  <Select value={formData.opening_balance_type} onValueChange={(v) => handleInputChange('opening_balance_type', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="TO_COLLECT">To Collect (Receivable)</SelectItem>
                      <SelectItem value="TO_PAY">To Pay (Payable)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Credit Period (Days)</Label>
                  <Input
                    type="number"
                    value={formData.credit_period_days}
                    onChange={(e) => handleInputChange('credit_period_days', parseInt(e.target.value))}
                  />
                </div>
                
                <div>
                  <Label>Credit Limit</Label>
                  <Input
                    type="number"
                    value={formData.credit_limit}
                    onChange={(e) => handleInputChange('credit_limit', parseFloat(e.target.value))}
                  />
                </div>
              </div>
            </div>
          )}

          {/* CONTACT PERSONS TAB */}
          {activeTab === 'contacts' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-700">Additional Contacts</h3>
                <Button type="button" size="sm" onClick={handleAddContactPerson}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Contact
                </Button>
              </div>

              {contactPersons.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-8">No additional contacts added</p>
              ) : (
                <div className="space-y-4">
                  {contactPersons.map((contact, index) => (
                    <div key={contact.id} className="border rounded-lg p-4 relative">
                      <button
                        onClick={() => handleRemoveContactPerson(contact.id)}
                        className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      
                      <div className="grid grid-cols-3 gap-3 mb-3">
                        <div>
                          <Label className="text-xs">Salutation</Label>
                          <Select 
                            value={contact.salutation} 
                            onValueChange={(v) => handleContactPersonChange(contact.id, 'salutation', v)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent>
                              {SALUTATIONS.map(s => (
                                <SelectItem key={s} value={s}>{s}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">First Name</Label>
                          <Input
                            value={contact.first_name}
                            onChange={(e) => handleContactPersonChange(contact.id, 'first_name', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Last Name</Label>
                          <Input
                            value={contact.last_name}
                            onChange={(e) => handleContactPersonChange(contact.id, 'last_name', e.target.value)}
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <Label className="text-xs">Email</Label>
                          <Input
                            type="email"
                            value={contact.email}
                            onChange={(e) => handleContactPersonChange(contact.id, 'email', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Work Phone</Label>
                          <Input
                            value={contact.work_phone}
                            onChange={(e) => handleContactPersonChange(contact.id, 'work_phone', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Mobile</Label>
                          <Input
                            value={contact.mobile}
                            onChange={(e) => handleContactPersonChange(contact.id, 'mobile', e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* OTHER TAB */}
          {activeTab === 'other' && (
            <div className="space-y-4">
              <div>
                <Label>Remarks / Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  rows={4}
                  placeholder="Add any additional notes..."
                />
              </div>

              <div>
                <Label>Reporting Tags</Label>
                <Input
                  placeholder="Comma-separated tags (e.g., VIP, Premium, Regular)"
                  value={formData.reporting_tags?.join(', ') || ''}
                  onChange={(e) => handleInputChange('reporting_tags', e.target.value.split(',').map(t => t.trim()))}
                />
              </div>

              <div className="border-t pt-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Custom Fields</h3>
                <p className="text-xs text-gray-500">Custom fields can be configured from Settings</p>
              </div>
            </div>
          )}
      </StandardPremiumModal>
  );
};

export default EnhancedAddPartyModal;
